import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './LeadSourceAnalytics.css';

// Color palette for specific lead sources
const LEAD_SOURCE_COLORS = {
  'Linkedin': '#0077B5',
  'Instagram': '#E4405F',
  'TikTok': '#010101',
  'YouTube': '#FF0000',
  'Search Engine': '#4285F4',
  'Client Referral': '#34D399',
  'Email Text Marketing': '#FBBF24',
  'Previous Contact': '#8B5CF6',
  'SITECH North Central Florida': '#FF6B35',
  'SITECH Northeast': '#FF8C5A',
  'SITECH Chesapeake': '#FFAD7E',
  'SITECH Midway': '#FFB88C',
  'SITECH Horizon': '#FFC39A',
  'SITECH Precision': '#FFCEA8',
  'SITECH (Other)': '#FFB347',
  'James River': '#FFD9B6',
  'Southeastern Equipment': '#FFE4C4',
  'Unknown': '#6B7280'
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Category colors
const CATEGORY_COLORS = {
  'Social Media': '#3B82F6',
  'SITECH Dealers': '#F59E0B',
  'Referrals': '#10B981',
  'Marketing': '#8B5CF6',
  'Unknown': '#6B7280'
};

// Map lead sources to categories
const getCategory = (leadSource) => {
  if (!leadSource) return 'Unknown';

  // Social Media
  if (['Linkedin', 'Instagram', 'TikTok', 'YouTube'].includes(leadSource)) {
    return 'Social Media';
  }

  // SITECH Dealers (includes "SITECH (Other)" from resolved sources)
  if (leadSource.startsWith('SITECH') ||
      ['James River', 'Southeastern Equipment'].includes(leadSource)) {
    return 'SITECH Dealers';
  }

  // Referrals
  if (['Client Referral', 'Previous Contact'].includes(leadSource)) {
    return 'Referrals';
  }

  // Marketing
  if (['Email Text Marketing', 'Search Engine'].includes(leadSource)) {
    return 'Marketing';
  }

  return 'Unknown';
};

// Resolve lead source from client data (use lead_source, fall back to acquisition_source + dealer_name)
const resolveLeadSource = (client) => {
  if (client.lead_source) return client.lead_source;

  // Check dealer_name first for SITECH dealers
  if (client.dealer_name) {
    const dealerName = client.dealer_name.toLowerCase();
    if (dealerName.includes('sitech north') && dealerName.includes('central florida')) {
      return 'SITECH North Central Florida';
    }
    if (dealerName.includes('sitech')) {
      return 'SITECH (Other)';
    }
  }

  if (client.acquisition_source) {
    const acqSource = client.acquisition_source.toLowerCase();

    // Dealer sources - check dealer_name for specifics
    if (acqSource === 'dealer') {
      if (client.dealer_name) {
        const dealerName = client.dealer_name.toLowerCase();
        if (dealerName.includes('sitech')) return 'SITECH (Other)';
      }
      return 'SITECH (Other)'; // Default dealer to SITECH
    }

    // Map legacy acquisition_source values
    const mapping = {
      'social-media': 'Linkedin', // Most social media is LinkedIn
      'Social Media': 'Linkedin',
      'search-engine': 'Search Engine',
      'current-client': 'Previous Contact',
      'current customer': 'Previous Contact',
      'current ab civil client': 'Previous Contact',
      'inside source': 'Previous Contact',
      'subcontractor wargo ent': 'Client Referral',
      'other': 'Client Referral' // "Other" with colleague details = referral
    };

    return mapping[acqSource] || mapping[client.acquisition_source] || client.acquisition_source;
  }

  return null;
};

const LeadSourceAnalytics = ({ clients, projects, chartColors }) => {
  const [selectedYear, setSelectedYear] = useState('all');
  const [viewMode, setViewMode] = useState('clients'); // 'clients' or 'revenue'
  const [activeOnly, setActiveOnly] = useState(false); // filter to active clients only
  const [newClientsViewMode, setNewClientsViewMode] = useState('clients'); // toggle for New Clients chart
  const [newClientsActiveOnly, setNewClientsActiveOnly] = useState(false); // active only toggle for New Clients chart

  // Get available years from client data
  const availableYears = useMemo(() => {
    const years = new Set();
    clients.forEach(client => {
      if (client.first_model_date) {
        years.add(new Date(client.first_model_date).getFullYear());
      }
      if (client.created_at) {
        years.add(new Date(client.created_at).getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [clients]);

  // Create a map of client revenue from 3D model projects only
  const clientRevenueMap = useMemo(() => {
    const revenueMap = {};

    projects.forEach(project => {
      if (!project.clientId) return;
      if (!revenueMap[project.clientId]) {
        revenueMap[project.clientId] = {
          total: 0,
          byYear: {}
        };
      }
      const price = parseFloat(project.tnRevenue ?? project.modelPrice) || 0;
      if (isNaN(price)) return;
      revenueMap[project.clientId].total += price;

      if (project.initialAcceptance) {
        const year = new Date(project.initialAcceptance).getFullYear();
        if (!revenueMap[project.clientId].byYear[year]) {
          revenueMap[project.clientId].byYear[year] = 0;
        }
        revenueMap[project.clientId].byYear[year] += price;
      }
    });

    return revenueMap;
  }, [projects]);

  // Filter to only 3D Model clients and exclude "Name Change" lead sources
  // Includes clients with "3D Models" or "3D Models and Takeoffs", excludes "Takeoffs" only
  const modelClients = useMemo(() => {
    return clients.filter(client => {
      // Exclude "Name Change" lead sources - these are company renames, not new leads
      const leadSource = resolveLeadSource(client);
      if (leadSource === 'Name Change') return false;

      // Filter by active status if toggle is on
      if (activeOnly) {
        const status = client.client_status;
        if (status !== 'Active Client' && status !== 'Part Time Client') return false;
      }

      // Only include clients with 3D Models service (excludes Takeoffs-only clients)
      const services = client.services_provided || [];
      return services.some(s => s === '3D Models' || s.includes('3D Models'));
    });
  }, [clients, activeOnly]);

  // Filter clients and calculate revenue based on selected year
  // When a year is selected: show clients who had revenue in that year, with only that year's revenue
  // When "all" is selected: show all clients with their total revenue
  const filteredClientsWithRevenue = useMemo(() => {
    return modelClients.map(client => {
      const clientRevenue = clientRevenueMap[client.id];

      let yearRevenue = 0;
      if (selectedYear === 'all') {
        yearRevenue = clientRevenue?.total || 0;
      } else {
        yearRevenue = clientRevenue?.byYear?.[selectedYear] || 0;
      }

      // Ensure it's a valid number
      if (isNaN(yearRevenue)) yearRevenue = 0;

      return {
        ...client,
        yearRevenue
      };
    }).filter(client => {
      // When a year is selected, only include clients who had revenue that year
      if (selectedYear !== 'all') {
        return client.yearRevenue > 0;
      }
      return true;
    });
  }, [modelClients, clientRevenueMap, selectedYear]);

  // For backward compatibility
  const filteredClients = filteredClientsWithRevenue;

  // Aggregate by specific lead source for pie chart (with revenue)
  const leadSourceData = useMemo(() => {
    const data = {};

    filteredClientsWithRevenue.forEach(client => {
      const source = resolveLeadSource(client) || 'Unknown';
      const revenue = client.yearRevenue || 0;

      if (!data[source]) {
        data[source] = { count: 0, revenue: 0 };
      }
      data[source].count += 1;
      data[source].revenue += revenue;
    });

    return Object.entries(data)
      .map(([name, values]) => ({
        name,
        count: values.count,
        revenue: Math.round(values.revenue * 100) / 100,
        value: viewMode === 'revenue' ? values.revenue : values.count,
        color: LEAD_SOURCE_COLORS[name] || '#6B7280'
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredClientsWithRevenue, viewMode]);

  // Aggregate by category for pie chart (with revenue)
  const categoryData = useMemo(() => {
    const data = {};

    filteredClientsWithRevenue.forEach(client => {
      const source = resolveLeadSource(client);
      const category = getCategory(source);
      const revenue = client.yearRevenue || 0;

      if (!data[category]) {
        data[category] = { count: 0, revenue: 0 };
      }
      data[category].count += 1;
      data[category].revenue += revenue;
    });

    return Object.entries(data)
      .map(([name, values]) => ({
        name,
        count: values.count,
        revenue: Math.round(values.revenue * 100) / 100,
        value: viewMode === 'revenue' ? values.revenue : values.count,
        color: CATEGORY_COLORS[name] || '#6B7280'
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredClientsWithRevenue, viewMode]);

  // Base data for New Clients chart (unfiltered) - used for locked Y-axis scale
  const yearlyTrendDataAll = useMemo(() => {
    const yearData = {};

    // Use 3D Model clients, excluding Name Change and takeoff-only clients (but NOT filtered by active status)
    const allValidClients = clients.filter(client => {
      const leadSource = resolveLeadSource(client);
      if (leadSource === 'Name Change') return false;

      const services = client.services_provided || [];
      return services.some(s => s === '3D Models' || s.includes('3D Models'));
    });

    allValidClients.forEach(client => {
      if (!client.first_model_date) return;

      const year = new Date(client.first_model_date).getFullYear();
      const source = resolveLeadSource(client);
      const category = getCategory(source);
      const clientRevenue = clientRevenueMap[client.id];
      const revenue = clientRevenue ? clientRevenue.total : 0;

      if (!yearData[year]) {
        yearData[year] = {
          year,
          'Social Media': 0,
          'SITECH Dealers': 0,
          'Referrals': 0,
          'Marketing': 0,
          'Unknown': 0,
          'Social Media Revenue': 0,
          'SITECH Dealers Revenue': 0,
          'Referrals Revenue': 0,
          'Marketing Revenue': 0,
          'Unknown Revenue': 0
        };
      }

      yearData[year][category] = (yearData[year][category] || 0) + 1;
      yearData[year][`${category} Revenue`] = (yearData[year][`${category} Revenue`] || 0) + revenue;
    });

    return Object.values(yearData).sort((a, b) => a.year - b.year);
  }, [clients, clientRevenueMap]);

  // Calculate max values for locked Y-axis scale
  const yearlyTrendMaxValues = useMemo(() => {
    let maxClients = 0;
    let maxRevenue = 0;

    yearlyTrendDataAll.forEach(yearData => {
      const totalClients = (yearData['Social Media'] || 0) + (yearData['SITECH Dealers'] || 0) +
                          (yearData['Referrals'] || 0) + (yearData['Marketing'] || 0) + (yearData['Unknown'] || 0);
      const totalRevenue = (yearData['Social Media Revenue'] || 0) + (yearData['SITECH Dealers Revenue'] || 0) +
                          (yearData['Referrals Revenue'] || 0) + (yearData['Marketing Revenue'] || 0) + (yearData['Unknown Revenue'] || 0);

      if (totalClients > maxClients) maxClients = totalClients;
      if (totalRevenue > maxRevenue) maxRevenue = totalRevenue;
    });

    return { maxClients, maxRevenue };
  }, [yearlyTrendDataAll]);

  // Filtered data for New Clients chart (respects active only toggle)
  const yearlyTrendData = useMemo(() => {
    if (!newClientsActiveOnly) {
      return yearlyTrendDataAll;
    }

    const yearData = {};

    const filteredClients = clients.filter(client => {
      const leadSource = resolveLeadSource(client);
      if (leadSource === 'Name Change') return false;

      const services = client.services_provided || [];
      if (!services.some(s => s === '3D Models' || s.includes('3D Models'))) return false;

      // Exclude Part Time and Lost clients
      const status = client.client_status;
      if (status === 'Part Time Client' || status === 'Lost') return false;

      return true;
    });

    filteredClients.forEach(client => {
      if (!client.first_model_date) return;

      const year = new Date(client.first_model_date).getFullYear();
      const source = resolveLeadSource(client);
      const category = getCategory(source);
      const clientRevenue = clientRevenueMap[client.id];
      const revenue = clientRevenue ? clientRevenue.total : 0;

      if (!yearData[year]) {
        yearData[year] = {
          year,
          'Social Media': 0,
          'SITECH Dealers': 0,
          'Referrals': 0,
          'Marketing': 0,
          'Unknown': 0,
          'Social Media Revenue': 0,
          'SITECH Dealers Revenue': 0,
          'Referrals Revenue': 0,
          'Marketing Revenue': 0,
          'Unknown Revenue': 0
        };
      }

      yearData[year][category] = (yearData[year][category] || 0) + 1;
      yearData[year][`${category} Revenue`] = (yearData[year][`${category} Revenue`] || 0) + revenue;
    });

    return Object.values(yearData).sort((a, b) => a.year - b.year);
  }, [clients, clientRevenueMap, newClientsActiveOnly, yearlyTrendDataAll]);

  // Total revenue by year (3D model clients' revenue attributed to the year it was earned)
  // This is CONTROLLED by the active only filter
  // Shows revenue growth over time by lead source category
  const yearlyRevenueData = useMemo(() => {
    const yearData = {};

    // Use filtered clients (respects active only filter, 3D Models clients only)
    modelClients.forEach(client => {
      const source = resolveLeadSource(client);
      const category = getCategory(source);
      const clientRevenue = clientRevenueMap[client.id];

      if (clientRevenue && clientRevenue.byYear) {
        // Attribute revenue to the year it was earned
        Object.entries(clientRevenue.byYear).forEach(([year, revenue]) => {
          const yearNum = parseInt(year);
          if (!yearData[yearNum]) {
            yearData[yearNum] = {
              year: yearNum,
              'Social Media': 0,
              'SITECH Dealers': 0,
              'Referrals': 0,
              'Marketing': 0,
              'Unknown': 0,
              total: 0
            };
          }
          yearData[yearNum][category] = (yearData[yearNum][category] || 0) + revenue;
          yearData[yearNum].total += revenue;
        });
      }
    });

    return Object.values(yearData).sort((a, b) => a.year - b.year);
  }, [modelClients, clientRevenueMap]);

  // Calculate totals for summary
  const totals = useMemo(() => {
    let totalClients = filteredClientsWithRevenue.length;
    let totalRevenue = 0;
    let unknownClients = 0;
    let unknownRevenue = 0;

    filteredClientsWithRevenue.forEach(client => {
      const source = resolveLeadSource(client);
      totalRevenue += client.yearRevenue;

      if (!source) {
        unknownClients += 1;
        unknownRevenue += client.yearRevenue;
      }
    });

    return { totalClients, totalRevenue, unknownClients, unknownRevenue };
  }, [filteredClientsWithRevenue]);

  // Custom tooltip for pie charts
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const totalValue = viewMode === 'revenue' ? totals.totalRevenue : totals.totalClients;
      const percentage = totalValue > 0 ? ((data.value / totalValue) * 100).toFixed(1) : 0;

      return (
        <div className="lead-source-tooltip">
          <p className="tooltip-name">{data.name}</p>
          <p className="tooltip-value">
            {data.count} clients ({viewMode === 'clients' ? `${percentage}%` : formatCurrency(data.revenue)})
          </p>
          {viewMode === 'clients' && (
            <p className="tooltip-revenue">{formatCurrency(data.revenue)} revenue</p>
          )}
          {viewMode === 'revenue' && (
            <p className="tooltip-detail">{percentage}% of total revenue</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for bar chart
  const CustomBarTooltip = ({ active, payload, label, coordinate }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const clientTotal = (data['Social Media'] || 0) + (data['SITECH Dealers'] || 0) +
                          (data['Referrals'] || 0) + (data['Marketing'] || 0) + (data['Unknown'] || 0);
      const revenueTotal = (data['Social Media Revenue'] || 0) + (data['SITECH Dealers Revenue'] || 0) +
                           (data['Referrals Revenue'] || 0) + (data['Marketing Revenue'] || 0) + (data['Unknown Revenue'] || 0);

      return (
        <div className="lead-source-tooltip" style={{
          transform: 'translateX(20px) translateY(-100%)',
          marginTop: '-10px'
        }}>
          <p className="tooltip-name">{label}</p>
          <p className="tooltip-total">
            {clientTotal} new clients • <strong>{formatCurrency(revenueTotal)}</strong>
          </p>
          {payload.map((entry, index) => {
            const category = entry.name.replace(' Revenue', '');
            const clients = data[category] || 0;
            const revenue = data[`${category} Revenue`] || 0;
            if (clients === 0) return null;
            return (
              <p key={index} className="tooltip-item" style={{ color: entry.color }}>
                {category}: {clients} ({formatCurrency(revenue)})
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Custom label for pie chart
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null; // Don't show labels for small slices

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="lead-source-analytics">
      {/* Controls Row */}
      <div className="controls-row">
        <div className="controls-left">
          <div className="year-selector">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              style={{
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                background: 'var(--surface-tertiary)',
                border: '1px solid var(--border-primary)',
                borderRadius: '0.375rem',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                minWidth: '120px'
              }}
            >
              <option value="all">All Time</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="view-mode-toggle">
            <button
              className={`view-button ${viewMode === 'clients' ? 'active' : ''}`}
              onClick={() => setViewMode('clients')}
            >
              By Clients
            </button>
            <button
              className={`view-button ${viewMode === 'revenue' ? 'active' : ''}`}
              onClick={() => setViewMode('revenue')}
            >
              By Revenue
            </button>
          </div>
          <div className="view-mode-toggle">
            <button
              className={`view-button ${!activeOnly ? 'active' : ''}`}
              onClick={() => setActiveOnly(false)}
            >
              All Clients
            </button>
            <button
              className={`view-button ${activeOnly ? 'active' : ''}`}
              onClick={() => setActiveOnly(true)}
            >
              Active Only
            </button>
          </div>
        </div>
        <div className="stats-summary">
          <span className="stat-item">
            <strong>{totals.totalClients}</strong> Clients
          </span>
          <span className="stat-item">
            <strong>{formatCurrency(totals.totalRevenue)}</strong> Revenue
          </span>
          {totals.unknownClients > 0 && (
            <span className="stat-item unknown">
              <strong>{totals.unknownClients}</strong> Unknown ({formatCurrency(totals.unknownRevenue)})
            </span>
          )}
        </div>
      </div>

      {/* Pie Charts Row */}
      <div className="charts-row pie-charts">
        {/* Specific Lead Sources */}
        <div className="chart-card">
          <h3 className="chart-title">Lead Sources (Specific)</h3>
          <div className="pie-chart-container">
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={leadSourceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={130}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {leadSourceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="pie-legend">
            {leadSourceData.slice(0, 10).map((entry, index) => (
              <div key={index} className="legend-item">
                <span className="legend-color" style={{ backgroundColor: entry.color }}></span>
                <span className="legend-label">{entry.name}</span>
                <span className="legend-value">
                  {entry.count} • {formatCurrency(entry.revenue)}
                </span>
              </div>
            ))}
            {leadSourceData.length > 10 && (
              <div className="legend-item">
                <span className="legend-label">+ {leadSourceData.length - 10} more</span>
              </div>
            )}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="chart-card">
          <h3 className="chart-title">Lead Sources (Categories)</h3>
          <div className="pie-chart-container">
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={130}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="pie-legend">
            {categoryData.map((entry, index) => (
              <div key={index} className="legend-item">
                <span className="legend-color" style={{ backgroundColor: entry.color }}></span>
                <span className="legend-label">{entry.name}</span>
                <span className="legend-value">
                  {entry.count} • {formatCurrency(entry.revenue)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Total Revenue by Year & Category */}
      <div className="chart-card full-width">
        <h3 className="chart-title">Total Revenue by Year & Lead Source Category</h3>
        <div className="bar-chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={yearlyRevenueData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors?.borderPrimary || '#4B5563'} />
              <XAxis
                dataKey="year"
                stroke={chartColors?.borderPrimary || '#4B5563'}
                tick={{ fill: chartColors?.textPrimary || '#F9FAFB' }}
              />
              <YAxis
                stroke={chartColors?.borderPrimary || '#4B5563'}
                tick={{ fill: chartColors?.textPrimary || '#F9FAFB' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="lead-source-tooltip" style={{
                        transform: 'translateX(20px) translateY(-100%)',
                        marginTop: '-10px'
                      }}>
                        <p className="tooltip-name">{label}</p>
                        <p className="tooltip-total">
                          Total: <strong>{formatCurrency(data.total)}</strong>
                        </p>
                        {payload.map((entry, index) => {
                          if (entry.value === 0) return null;
                          return (
                            <p key={index} className="tooltip-item" style={{ color: entry.color }}>
                              {entry.name}: {formatCurrency(entry.value)}
                            </p>
                          );
                        })}
                      </div>
                    );
                  }
                  return null;
                }}
                cursor={{ fill: 'transparent' }}
                wrapperStyle={{ pointerEvents: 'none', zIndex: 1000 }}
                allowEscapeViewBox={{ x: true, y: true }}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="rect"
              />
              <Bar dataKey="Social Media" stackId="a" fill={CATEGORY_COLORS['Social Media']} />
              <Bar dataKey="SITECH Dealers" stackId="a" fill={CATEGORY_COLORS['SITECH Dealers']} />
              <Bar dataKey="Referrals" stackId="a" fill={CATEGORY_COLORS['Referrals']} />
              <Bar dataKey="Marketing" stackId="a" fill={CATEGORY_COLORS['Marketing']} />
              <Bar dataKey="Unknown" stackId="a" fill={CATEGORY_COLORS['Unknown']} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Separator for independent section */}
      <div style={{
        width: '100%',
        margin: '2rem 0',
        padding: '1rem 0',
        borderTop: '2px solid var(--border-primary)',
        textAlign: 'center'
      }}>
        <span style={{
          background: 'var(--surface-primary)',
          padding: '0 1rem',
          color: 'var(--text-secondary)',
          fontSize: '0.875rem',
          fontWeight: 500
        }}>
          Historical New Client Acquisition (Not Affected by Filters Above)
        </span>
      </div>

      {/* Yearly Trend Bar Chart - New Clients */}
      <div className="chart-card full-width">
        <div className="chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 className="chart-title" style={{ margin: 0 }}>New Clients by Year & Category</h3>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="view-mode-toggle">
              <button
                className={`view-button ${!newClientsActiveOnly ? 'active' : ''}`}
                onClick={() => setNewClientsActiveOnly(false)}
              >
                All
              </button>
              <button
                className={`view-button ${newClientsActiveOnly ? 'active' : ''}`}
                onClick={() => setNewClientsActiveOnly(true)}
              >
                Active Only
              </button>
            </div>
            <div className="view-mode-toggle">
              <button
                className={`view-button ${newClientsViewMode === 'clients' ? 'active' : ''}`}
                onClick={() => setNewClientsViewMode('clients')}
              >
                # Clients
              </button>
              <button
                className={`view-button ${newClientsViewMode === 'revenue' ? 'active' : ''}`}
                onClick={() => setNewClientsViewMode('revenue')}
              >
                Revenue
              </button>
            </div>
          </div>
        </div>
        <div className="bar-chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={yearlyTrendData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors?.borderPrimary || '#4B5563'} />
              <XAxis
                dataKey="year"
                stroke={chartColors?.borderPrimary || '#4B5563'}
                tick={{ fill: chartColors?.textPrimary || '#F9FAFB' }}
              />
              <YAxis
                stroke={chartColors?.borderPrimary || '#4B5563'}
                tick={{ fill: chartColors?.textPrimary || '#F9FAFB' }}
                tickFormatter={newClientsViewMode === 'revenue' ? (value) => `$${(value / 1000).toFixed(0)}k` : undefined}
                domain={[0, newClientsViewMode === 'revenue' ? yearlyTrendMaxValues.maxRevenue : yearlyTrendMaxValues.maxClients]}
              />
              <Tooltip
                content={<CustomBarTooltip />}
                cursor={{ fill: 'transparent' }}
                wrapperStyle={{ pointerEvents: 'none', zIndex: 1000 }}
                allowEscapeViewBox={{ x: true, y: true }}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="rect"
              />
              {newClientsViewMode === 'clients' ? (
                <>
                  <Bar dataKey="Social Media" stackId="a" fill={CATEGORY_COLORS['Social Media']} />
                  <Bar dataKey="SITECH Dealers" stackId="a" fill={CATEGORY_COLORS['SITECH Dealers']} />
                  <Bar dataKey="Referrals" stackId="a" fill={CATEGORY_COLORS['Referrals']} />
                  <Bar dataKey="Marketing" stackId="a" fill={CATEGORY_COLORS['Marketing']} />
                  <Bar dataKey="Unknown" stackId="a" fill={CATEGORY_COLORS['Unknown']} />
                </>
              ) : (
                <>
                  <Bar dataKey="Social Media Revenue" name="Social Media" stackId="a" fill={CATEGORY_COLORS['Social Media']} />
                  <Bar dataKey="SITECH Dealers Revenue" name="SITECH Dealers" stackId="a" fill={CATEGORY_COLORS['SITECH Dealers']} />
                  <Bar dataKey="Referrals Revenue" name="Referrals" stackId="a" fill={CATEGORY_COLORS['Referrals']} />
                  <Bar dataKey="Marketing Revenue" name="Marketing" stackId="a" fill={CATEGORY_COLORS['Marketing']} />
                  <Bar dataKey="Unknown Revenue" name="Unknown" stackId="a" fill={CATEGORY_COLORS['Unknown']} />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default LeadSourceAnalytics;
